"use client";

import { useSyncExternalStore } from "react";
import { PACK_BY_ID, type PackId } from "@/lib/packs";
import { usePosSession } from "@/lib/hooks/usePosSession";
import type { RailItem } from "@/components/shell/PosRail";

const KEY = "pos.activePack";

/** No external event ever invalidates these reads on their own — the value is settled once, right
 * after hydration, the same way an `as`/pack query param or `localStorage` read always has been
 * here. So there is nothing to subscribe to. */
const noSubscribe = () => () => {};
const noPack = (): PackId | undefined => undefined;

function readRememberedPack(): PackId | undefined {
  const url = new URLSearchParams(window.location.search).get("pack");
  const saved = url ?? window.localStorage.getItem(KEY);
  return saved && saved in PACK_BY_ID ? (saved as PackId) : undefined;
}

function readAsHostPack(): PackId | undefined {
  const url = new URLSearchParams(window.location.search).get("as");
  const saved = url ?? window.localStorage.getItem(KEY);
  return saved && saved in PACK_BY_ID && PACK_BY_ID[saved as PackId].reuses ? (saved as PackId) : undefined;
}

/**
 * Which vertical the operator is currently working in.
 *
 * The shared back-office screens (catalog, customers, shift, reports, settings) are the same
 * code for every vertical, so they used to render a neutral nav of their own. The effect was
 * that walking from Restaurant → Menu silently dropped you out of the restaurant: the rail
 * changed under you and there was no way back to the floor. For a prototype meant to be
 * demoed as one connected product, that reads as broken navigation.
 *
 * So a pack screen records itself here, and the shared screens borrow that pack's rail. The
 * operator stays inside their business the whole way round.
 *
 * In production a tenant is entitled to exactly one pack (ADR-0014) and this becomes a value
 * on `Context` from the BFF — same hook, no screen changes.
 *
 * That is now what happens: {@link useActivePack} takes the counter's pack from `/pos/context` and
 * uses this remembered value only until the context answers. See SWEEP-1 S2 for what the remembered
 * value did on its own.
 */
export function rememberActivePack(pack: PackId) {
  if (typeof window !== "undefined") window.localStorage.setItem(KEY, pack);
}

/**
 * The remembered pack and its rail, for a screen shared across verticals.
 *
 * `fallbackNav` is used until the pack is known (first paint, or a fresh browser that has not
 * visited a pack screen yet), so nothing flashes empty.
 */
export function useActivePack(fallbackNav: RailItem[]): {
  pack?: PackId;
  /** The pack whose data to show — an expansion vertical resolves to the core pack it reuses. */
  hostPack?: PackId;
  nav: RailItem[];
} {
  /**
   * `remembered` comes from the URL/`localStorage` — state the server cannot see. Reading it with
   * `useSyncExternalStore` (server snapshot: no pack) keeps the very first client render matching
   * the server-rendered markup, and only reveals the browser's answer once mounted, without an
   * Effect calling `setState` underneath it. See "You Might Not Need an Effect" /
   * `useSyncExternalStore` for reading external, non-reactive browser state like this.
   */
  const remembered = useSyncExternalStore(noSubscribe, readRememberedPack, noPack);
  const { homePack } = usePosSession();

  /**
   * The counter wins. SWEEP-1 S2: `pos.activePack` outlives a shift, and a till is shared, so the
   * remembered value is the PREVIOUS cashier's vertical. Measured on this stack — a browser holding
   * `restaurant` from an earlier shift, signed in fresh as the Gym Counter at BLR-G1, was shown the
   * restaurant console: Dine-in/Take-away, table T-07, Filter Coffee and Masala Dosa, with "Gym
   * Counter" in the rail. `/customers` read "Guests" until `/gym` was opened, then "Members".
   *
   * The remembered value still has a job — an expansion vertical borrowing a host pack's screens
   * needs to say which of them it is, and that cannot come from the entitlement alone — so it is
   * kept, but only when it does not contradict the counter. Anything that does is the last shift's
   * answer to a question this shift did not ask.
   */
  const pack = remembered && (remembered === homePack || PACK_BY_ID[remembered]?.reuses === homePack)
    ? remembered
    : (homePack ?? remembered);

  /**
   * `hostPack` is the vertical whose DATA this screen should show. An expansion vertical borrows a
   * core pack's screens (ADR-0014), so a coaching centre must see the gym's catalogue rows — under
   * its own words. Scoping by the expansion id instead left them with an empty catalog.
   */
  const hostPack = pack ? (PACK_BY_ID[pack].reuses ?? pack) : undefined;

  return { pack, hostPack, nav: pack ? PACK_BY_ID[pack].nav : fallbackNav };
}

/**
 * Carry the active vertical across an in-app navigation.
 *
 * An expansion vertical's rail points at host routes (derma's "Today's list" is /clinic), and the
 * host screen passes its own literal pack. A link without `?as=` therefore drops the operator into
 * the host — and the host then remembers itself, so they cannot get back. Any screen that pushes
 * or links to another screen must go through this.
 */
export function useWithPack(): (href: string) => string {
  const pack = useSyncExternalStore(noSubscribe, readAsHostPack, noPack);
  return (href: string) => (pack && !href.includes("?") ? `${href}?as=${pack}` : pack ? `${href}&as=${pack}` : href);
}
